import { Address } from './address';
import { Professional, ProfessionalBaseType } from './professional';
import { MediumDeliveryError } from './sending';
import { RecipientPrediction, VerifiableType } from './sendingRequest.types';
import { Telecom } from './telecom';

/**
 * The property @sentToFallback can be :
 * - 'yes' if there is a resent
 * - 'no' if there isn't a resent
 * - 'error' if the resent is in error
 */
export type MediumDeliveryStatusWithError = {
  status: MediumDeliveryStatusEnum.Error;
  error: MediumDeliveryError;
  sentToFallback?: SentToFallbackStatus;
};

/**
 * The status Yes, SkippedNoMedium or SkippedAlreadySent
 * indicate that the sender must have a 'yes_if_bounce' or
 * 'yes_if_bounce_or_unread' as patientAutomaticResending
 */
export enum SentToFallbackStatus {
  Yes = 'yes',
  No = 'no',
  SkippedNoMedium = 'no_fallback_medium',
  SkippedAlreadySent = 'already_sent',
}

export enum MediumDeliveryStatusEnum {
  Pending = 'pending',
  Success = 'success',
  Read = 'read',
  Error = 'error',
}
export type MediumDeliveryStatusOnly = {
  status:
    | MediumDeliveryStatusEnum.Pending
    | MediumDeliveryStatusEnum.Success
    | MediumDeliveryStatusEnum.Read;
  sentToFallback?:
    | SentToFallbackStatus.Yes
    | SentToFallbackStatus.No
    | SentToFallbackStatus.SkippedAlreadySent
    | SentToFallbackStatus.SkippedNoMedium;
};

export type MediumDeliveryStatus = {
  mediumId: string;
  bounced?: boolean;
  resendDocumentReference?: string;
  resendToTrustedContacts?: boolean;
} & (MediumDeliveryStatusWithError | MediumDeliveryStatusOnly);

export type Medium<T extends Telecom | Address> = T & {
  // We put de deliveryStatus to optionnal here
  // because of we use the sames DTOs to make
  // GET & PUT Requests
  // And to keep retro-compatibility with Lifen Mail V1.alpha
  deliveryStatus?: MediumDeliveryStatus;
  customMessage?: string;
  replaced?: boolean;
  conversationClosed?: boolean;
};

export type MediumList<T extends Telecom | Address> = Medium<T>[];

export enum RecipientVerificationTypes {
  recipients_other_than_high = 'recipients-other-than-high',
  recipients_without_medium = 'recipients-without-medium',
  recipients_organization_multiple_mss_telecoms = 'recipients-organization-multiple-mss-telecoms',
  recipient_context = 'recipient-context',
  recipient_address_incomplete = 'recipient-address-incomplete',
  recipient_added_with_inactive_telecom = 'recipient-added-with-inactive-telecom',
  recipient_not_in_directory = 'recipient-not-in-directory',
  recipient_with_multiple_postal_addresses = 'recipient-with-multiple-postal-addresses',
}

export type RecipientVerificationType =
  | RecipientVerificationTypes.recipients_other_than_high
  | RecipientVerificationTypes.recipients_without_medium
  | RecipientVerificationTypes.recipients_organization_multiple_mss_telecoms
  | RecipientVerificationTypes.recipient_context
  | RecipientVerificationTypes.recipient_address_incomplete
  | RecipientVerificationTypes.recipient_added_with_inactive_telecom
  | RecipientVerificationTypes.recipient_not_in_directory
  | RecipientVerificationTypes.recipient_with_multiple_postal_addresses;

export enum PatientRecipientVerificationTypes {
  email_telecom_without_mfa = 'email-telecom-without-mfa',
}

export type PatientRecipientVerificationType =
  PatientRecipientVerificationTypes.email_telecom_without_mfa;

export interface RecipientContext {
  idNat?: string;
  name?: string;
  address?: string;
}

export interface RecipientVerification {
  id: number;
  key: RecipientVerificationType | PatientRecipientVerificationType;
  verifiableId: number;
  verifiableType: VerifiableType;
  dismissedAt?: string;
  recipientContext?: RecipientContext;
}
export interface DocumentRecipientDto {
  id: string;
  contactId?: number;
  contactType?: 'organization' | 'practitioner';
  type: ProfessionalBaseType | 'Patient';
  source?: string;
  telecoms: MediumList<Telecom>;
  addresses: MediumList<Address>;
  prediction?: ExtractedPrediction;
  verifications: RecipientVerification[];
  practRolePractitionerReference?: string;
  isFavorite?: boolean;
  trustedContactRelationship?: string;
  trustedContactId?: string;
  name?: string;
}

export interface CreateDematRecipient {
  email: string;
  phoneNumber?: string;
  name: string;
}

export interface CreatePostalRecipient {
  recipientName: string;
  address: string;
  complementaryAdress?: string;
  complementaryAdressBis?: string;
  zipCode: string;
  city: string;
}

export interface DocumentRecipientBundle {
  id: string;
  contactId?: number;
  contactType?: 'organization' | 'practitioner';
  type: ProfessionalBaseType | 'Patient';
  source?: string;
  entity?: Professional;
  telecoms: MediumList<Telecom>;
  addresses: MediumList<Address>;
  prediction?: ExtractedPrediction;
  verifications: RecipientVerification[];
  isFavorite?: boolean;
  trustedContactRelationship?: string;
  trustedContactId?: string;
  name?: string;
}

export interface ExtractedPrediction {
  grade?: RecipientPrediction['predictionCertainty'];
  notCertainCase?: string;
  fromAddress: string;
}
